import { useAccount } from '@azure/msal-react'
import { useEffect, useId, useRef, useState } from 'react'

import LogoutIcon from '@/assets/icons/logout.svg'
import { authService } from '@/features/auth/auth-service'
import { cn } from '@/lib/utils'
import { Button } from '@/shared/components'

const getInitial = (name: string | undefined, username: string | undefined) => {
  const firstName = name?.trim().split(/\s+/)[0]
  const fromName = firstName?.[0]
  if (fromName) return fromName.toUpperCase()

  const fromUsername = username?.trim()[0]
  if (fromUsername) return fromUsername.toUpperCase()

  return '?'
}

export const ProfileMenu = () => {
  const account = useAccount()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const menuId = useId()

  const displayName = account?.name ?? account?.username ?? 'Signed in'
  const email = account?.username
  const initial = getInitial(account?.name, account?.username)

  useEffect(() => {
    if (!open) return

    const handlePointerDown = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label="Open profile menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex size-10 items-center justify-center rounded-full bg-primary-dark text-sm font-bold text-white cursor-pointer',
          'transition-colors hover:bg-primary-dark/90',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-focus'
        )}
      >
        {initial}
      </button>

      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label="Profile"
          className="absolute right-0 z-50 mt-2 w-64 rounded-xl border border-muted-foreground/20 bg-background p-4 shadow-sm"
        >
          <div className="flex flex-col gap-1 border-b border-muted-foreground/20 pb-3">
            <p className="text-sm font-medium text-foreground">{displayName}</p>
            {email ? (
              <p className="text-xs text-muted-foreground">{email}</p>
            ) : null}
          </div>

          <Button
            type="button"
            variant="light"
            size="sm"
            icon={LogoutIcon}
            role="menuitem"
            className="mt-3 w-full justify-start"
            onClick={() => {
              setOpen(false)
              void authService.logoutRedirect()
            }}
          >
            Logout
          </Button>
        </div>
      ) : null}
    </div>
  )
}
